import type { FileItem } from '@qse/utils'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'

export const useFileExplorerClipboard = (
  selectedFiles: Set<string | number>,
  onBulkCopy: (fileIds: string[]) => void,
  files?: FileItem[],
) => {
  const [clipboardFileIds, setClipboardFileIds] = useState<string[]>([])

  // Handle clipboard copy (Ctrl+C)
  const handleClipboardCopy = useCallback(async () => {
    if (selectedFiles.size === 0) return

    // Check if any of the selected files are folders
    if (files && files.length > 0) {
      const selectedFileItems = files.filter(file => selectedFiles.has(file.id))
      const foldersToCopy = selectedFileItems.filter(
        file => file.type === 'folder' || file.isDir === true,
      )

      if (foldersToCopy.length > 0) {
        // Show custom error message for folders
        if (foldersToCopy.length === 1) {
          toast.error(
            `Cannot copy folder "${foldersToCopy[0].name}". Folder copying is not supported.`,
          )
        } else {
          const folderNames = foldersToCopy.slice(0, 3).map(f => f.name)
          const remaining = foldersToCopy.length - 3
          const folderList =
            folderNames.join(', ') + (remaining > 0 ? ` and ${remaining} more` : '')

          toast.error(`Cannot copy folders: ${folderList}. Folder copying is not supported.`)
        }

        return // Exit early without copying to clipboard
      }
    }

    try {
      const fileIdStrings = Array.from(selectedFiles).map(id => id.toString())

      setClipboardFileIds(fileIdStrings)

      // Also copy to system clipboard
      await navigator.clipboard.writeText(
        JSON.stringify({
          type: 'qse-files',
          fileIds: fileIdStrings,
          timestamp: Date.now(),
        }),
      )

      toast.success(
        `Copied ${selectedFiles.size} file${selectedFiles.size === 1 ? '' : 's'} to clipboard`,
      )
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
      toast.error('Failed to copy to clipboard')
    }
  }, [selectedFiles, files])

  // Handle clipboard paste (Ctrl+V)
  const handleClipboardPaste = useCallback(async () => {
    // First try to use internal clipboard
    if (clipboardFileIds.length > 0 && onBulkCopy) {
      try {
        // Keep fileIds as strings - no need to convert to numbers
        await onBulkCopy(clipboardFileIds)
        toast.success(
          `Pasted ${clipboardFileIds.length} file${clipboardFileIds.length === 1 ? '' : 's'}`,
        )
        // Clear clipboard after successful paste
        setClipboardFileIds([])

        return
      } catch (error) {
        console.error('Failed to paste files:', error)
        // toast.error('Failed to paste files')

        return
      }
    }

    // If no internal clipboard, try to read from system clipboard
    try {
      const clipboardText = await navigator.clipboard.readText()
      const clipboardData = JSON.parse(clipboardText)

      if (clipboardData.type === 'qse-files' && clipboardData.fileIds && onBulkCopy) {
        // Keep fileIds as strings - no need to convert to numbers
        await onBulkCopy(clipboardData.fileIds)
        toast.success(
          `Pasted ${clipboardData.fileIds.length} file${clipboardData.fileIds.length === 1 ? '' : 's'}`,
        )

        return
      }
    } catch (error) {
      // Ignore clipboard read errors - they're expected if no QSE data
    }

    toast.error('No files in clipboard to paste')
  }, [clipboardFileIds, onBulkCopy])

  const clearClipboard = useCallback(() => {
    setClipboardFileIds([])
  }, [])

  return {
    clipboardFileIds,
    handleClipboardCopy,
    handleClipboardPaste,
    clearClipboard,
  }
}
